import { ChangeDetectorRef, NgModuleRef } from '@angular/core';
import { NgRedux } from '@angular-redux3/store';
import { App } from '@ps20-frontend/frontend-models';
import { hasReduxDevtoolsParam, isNonProdEnvironment } from '@ps20-frontend/utils';
import {
  BotInfo,
  BrowserInfo,
  detect,
  NodeInfo,
  ReactNativeInfo,
  SearchBotDeviceInfo,
} from 'detect-browser';
import { Reducer } from 'redux';
import { DevToolsEnhancerOptions as DevToolsOptions } from '@reduxjs/toolkit';
import { NgRefreshStore } from './ngRefreshStore';
import { GlobalEventDispatcherService } from './global-event-dispatcher.service';
import { AngebotsId } from './payload-models';
import { GlobalEventDispatcher, StoreRef } from './globalEventDispatcher';
import { GlobalActions } from './global-actions';

type browserTypes = BrowserInfo | SearchBotDeviceInfo | BotInfo | NodeInfo | ReactNativeInfo | null;

export interface StoreProvidingModule<T> {
  storeProvided: boolean;
  ngRefreshStore?: NgRefreshStore<T>;
}

export function getCurrentStoreWithangebotsId(
  globalEventDispatcherService: GlobalEventDispatcherService,
  appId: App,
  angebotsId: string,
): StoreRef {
  return globalEventDispatcherService.globalEventDispatcherRef.stores.find(
    store => store.appName === appId && store.angebotsId === angebotsId,
  );
}

export function storeIsAvailable({ angebotsId, appId }: AngebotsId): boolean {
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  return globalEventDispatcher.stores.some(
    store => store.angebotsId === angebotsId && store.appName === appId,
  );
}

// Substring tokens: key is redacted when its lowercase form contains any of these.
const PII_SUBSTRING_TOKENS = [
  'vorname', 'nachname', 'name', 'geburtsdatum', 'geburtsland',
  'iban', 'kontoinhaber', 'kontonummer',
  'strasse', 'hausnummer',
  'email', 'telefon', 'handy', 'mobil',
  'anschrift', 'adresse',
  'kundennummer', 'firmenname', 'namenszusatz',
  'tiername', 'institutname', 'underwritername',
  'staatsbschaft', 'famstand', 'anrede', 'adelstitel',
];

// Exact tokens: short tokens where substring matching would cause false positives
// (e.g. 'ort' ⊂ 'support', 'plz' ⊂ 'komplziert', 'bic' ⊂ 'public').
const PII_EXACT_TOKENS = new Set(['ort', 'plz', 'bic']);

// RISK-ACCEPTED: the generic `value` key (used in KommdatenChanged for email/telefon payloads)
// is not redacted — DevTools are developer-only (non-prod + explicit URL param).

function isPiiKey(key: string): boolean {
  const lower = key.toLowerCase();
  return PII_EXACT_TOKENS.has(lower) || PII_SUBSTRING_TOKENS.some(token => lower.includes(token));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function redactPiiFieldsRecursive(obj: any, seen: WeakSet<object>): any {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  if (seen.has(obj)) {
    return '[Circular]';
  }
  seen.add(obj);
  if (Array.isArray(obj)) {
    return obj.map(item => redactPiiFieldsRecursive(item, seen));
  }
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    result[key] = isPiiKey(key) ? '***' : redactPiiFieldsRecursive(obj[key], seen);
  }
  return result;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function redactPiiFields(obj: any): any {
  return redactPiiFieldsRecursive(obj, new WeakSet());
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const integrateReduxDevtoolsExtension = (appName: string): DevToolsOptions | false => {
  if (!isNonProdEnvironment() || !hasReduxDevtoolsParam()) {
    return false;
  }
  return {
    name: appName,
    actionsDenylist: getBlacklistActions(),
    stateSanitizer: redactPiiFields,
    actionSanitizer: redactPiiFields,
  };
};

export function getBlacklistActions(): string[] {
  const blacklist: string[] = [];
  quoteDBBlackListActions().forEach(quoteDBAction => blacklist.push(quoteDBAction));
  return blacklist;
}

function quoteDBBlackListActions(): string[] {
  return [
    GlobalActions.QUOTEDB_INIT,
    GlobalActions.QUOTEDB_DASHBOARD_DATA_REQUESTED,
    GlobalActions.QUOTEDB_DATA_DASHBOARD_GENERATED,
    GlobalActions.QUOTEDB_CHECKOUT_DATA_REQUESTED,
    GlobalActions.QUOTEDB_DATA_CHECKOUT_GENERATED,
    GlobalActions.QUOTEDB_TOOLBAR_DATA_REQUESTED,
    GlobalActions.QUOTEDB_DATA_TOOLBAR_GENERATED,
    GlobalActions.QUOTEDB_DATA_REQUESTED,
    GlobalActions.QUOTEDB_DATA_SPARTE_GENERATED,
  ];
}

export const detectBrowser = function (): browserTypes {
  return detect();
};

export const initReduxStore = function <T>(
  globalEventDispatcherService: GlobalEventDispatcherService,
  ngRedux: NgRedux<T>,
  moduleRef: NgModuleRef<StoreProvidingModule<T>>,
  changeDetectorRef: ChangeDetectorRef,
  appId: App,
  reducer: Reducer<T>,
): void {
  const currentStore = globalEventDispatcherService.globalEventDispatcherRef.stores.find(
    store => store.appName === appId,
  );
  ngRedux.provideStore(reducer, currentStore.store);
  moduleRef.instance.storeProvided = true;
  (currentStore.store as NgRefreshStore<T>).bindApp(changeDetectorRef);
};

export const initReduxMultiStore = function <T>(
  globalEventDispatcherService: GlobalEventDispatcherService,
  ngRedux: NgRedux<T>,
  moduleRef: NgModuleRef<StoreProvidingModule<T>>,
  changeDetectorRef: ChangeDetectorRef,
  appId: App,
  angebotsId: string,
  reducer: Reducer,
): void {
  const currentStore = getCurrentStoreWithangebotsId(
    globalEventDispatcherService,
    appId,
    angebotsId,
  ).store as NgRefreshStore<T>;

  const ngRefreshStore: NgRefreshStore<T> = new NgRefreshStore<T>(appId);
  moduleRef.instance.ngRefreshStore = ngRefreshStore;
  ngRefreshStore.setDelegate(currentStore.getDelegate());

  ngRedux.provideStore(reducer, ngRefreshStore);
  moduleRef.instance.storeProvided = true;
  currentStore.bindApp(changeDetectorRef);
};

export const refreshChangeDetector = <T>(
  globalEventDispatcherService: GlobalEventDispatcherService,
  changeDetectorRef: ChangeDetectorRef,
  appId: App,
  angebotsId: string,
): void => {
  const store = getCurrentStoreWithangebotsId(globalEventDispatcherService, appId, angebotsId)
    .store as NgRefreshStore<T>;
  store.bindApp(changeDetectorRef);
};
